import { useState, useEffect, useCallback } from "react";
import { getAuth } from "firebase/auth";
import { storeLoginTime } from "../utils/authUtils";

interface SessionExpiryState {
  showModal: boolean;
  timeLeft: number; // in minutes
  isExpired: boolean;
  hasWarned: boolean;
}

export const useSessionExpiryManager = () => {
  const [state, setState] = useState<SessionExpiryState>({
    showModal: false,
    timeLeft: 0,
    isExpired: false,
    hasWarned: false,
  });

  const checkSessionTime = useCallback(() => {
    const loginTimeStr = sessionStorage.getItem("user_login_time");

    if (!loginTimeStr) {
      return; // No login time stored
    }

    const loginTime = parseInt(loginTimeStr);
    const currentTime = Date.now();
    const twelveHours = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
    const warningTime = 10 * 60 * 60 * 1000; // 10 hours (2 hours warning)

    const elapsedTime = currentTime - loginTime;
    const timeLeft = Math.max(
      0,
      Math.floor((twelveHours - elapsedTime) / (60 * 1000))
    ); // in minutes

    // Session expired
    if (elapsedTime >= twelveHours) {
      setState((prev) => ({
        ...prev,
        showModal: true,
        timeLeft: 0,
        isExpired: true,
      }));
      return;
    }

    // Show warning when 2 hours or less remaining (and haven't warned yet)
    if (elapsedTime >= warningTime && !state.hasWarned) {
      setState((prev) => ({
        ...prev,
        showModal: true,
        timeLeft,
        isExpired: false,
        hasWarned: true,
      }));
      return;
    }

    // Update time left if modal is already showing
    if (state.showModal && !state.isExpired) {
      setState((prev) => ({
        ...prev,
        timeLeft,
      }));
    }
  }, [state.hasWarned, state.showModal, state.isExpired]);

  useEffect(() => {
    // Check immediately
    checkSessionTime();

    // Check every minute
    // const interval = setInterval(checkSessionTime, 60 * 1000);

    // return () => clearInterval(interval);
  }, [checkSessionTime]);

  const refreshSession = useCallback(() => {
    // Store new login time (reset the 12-hour timer)
    storeLoginTime();

    // Reset state
    setState({
      showModal: false,
      timeLeft: 0,
      isExpired: false,
      hasWarned: false,
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      // Clear all storage
      const itemsToRemove = [
        "firebase_token",
        "user_login_time",
        "user",
        "accessToken",
        "session_info",
      ];

      itemsToRemove.forEach((item) => {
        // sessionStorage.removeItem(item);
      });

      // Also clear localStorage
      const localStorageItems = ["user", "accessToken", "firebase_token"];
      localStorageItems.forEach((item) => {
        localStorage.removeItem(item);
      });

      // Sign out from Firebase
      const auth = getAuth();
      if (auth.currentUser) {
        await auth.signOut();
      }

      // Redirect to login
      window.location.href = "/";
    } catch (error) {
      console.error("Error during logout:", error);
      // Force redirect even if there's an error
      window.location.href = "/";
    }
  }, []);

  const dismissModal = useCallback(() => {
    // Only allow dismissing if not expired
    if (!state.isExpired) {
      setState((prev) => ({
        ...prev,
        showModal: false,
      }));
    }
  }, [state.isExpired]);

  return {
    ...state,
    refreshSession,
    logout,
    dismissModal,
  };
};
